import { Fragment, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  QueueListIcon,
  ArchiveBoxIcon,
  CreditCardIcon,
  ChartBarIcon,
  ChevronDoubleLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BellIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  ServerIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { canAccessNavHref } from '@/lib/dashboardAccess'
import { releaseStuckModalOverlays } from '@/lib/modalOverlayCleanup'
import { useTranslation } from '@/i18n/useTranslation'
import type { AppUiNamespace } from '@/i18n/appUiLocale'
import {
  activeNavGroupForPath,
  CUSTOMER_NAV_GROUP_ORDER,
  customerNavigationConfig,
  loadSidebarExpandedGroups,
  navGroupPanelId,
  navItemMatchesPath,
  persistSidebarExpandedGroups,
  sidebarExpandedStorageKey,
  STAFF_NAV_GROUP_ORDER,
  staffNavigationConfig,
  type NavigationItemConfig,
  type NavGroupKey,
} from '@/components/layout/sidebarNavConfig'
import { useSupportUnreadStore } from '@/stores/supportUnreadStore'
import { useShopPreferences } from '@/hooks/useShopPreferences'
import { useShopWriteLock } from '@/hooks/useShopWriteLock'
import {
  isCustomerPortalHrefEnabled,
  parseCustomerPortalSettings,
} from '@/lib/customerPortalSettings'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  isCustomer?: boolean
  desktopCollapsed?: boolean
  onDesktopCollapsedChange?: (collapsed: boolean) => void
}

const MD_MIN = 768

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  isCustomer = false,
  desktopCollapsed = false,
  onDesktopCollapsedChange,
}) => {
  const user = useAuthStore((s) => s.user)
  const { permissions, role } = usePermissionsStore()
  const { planCode, features: planFeatures } = useShopWriteLock()
  const supportUnreadThreads = useSupportUnreadStore((s) => s.unreadThreads)
  const { shop: shopContext } = useShopPreferences()
  const supportChatEnabled = shopContext?.support_chat_enabled !== false
  const rentalEnabled =
    shopContext?.rental_enabled === true && shopContext?.is_platform_shop !== true
  const customerPortalSettings = useMemo(
    () => parseCustomerPortalSettings(shopContext?.customer_portal_settings),
    [shopContext?.customer_portal_settings],
  )
  const shopPrimary = useMemo(() => {
    if (!isCustomer) return null
    const raw = String(shopContext?.primary_color || '').trim()
    return raw || '#4f46e5'
  }, [isCustomer, shopContext?.primary_color])
  const { pathname } = useLocation()
  const ns: AppUiNamespace = isCustomer ? 'customerPortal' : 'dashboard'
  const { t: tCommon } = useTranslation('common')
  const { t: tNav } = useTranslation(ns)

  const [navSearchQuery, setNavSearchQuery] = useState('')
  const [isDesktopViewport, setIsDesktopViewport] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(min-width: ${MD_MIN}px)`).matches : false,
  )

  const portal = isCustomer ? 'customer' : 'staff'
  const groupOrder = isCustomer ? CUSTOMER_NAV_GROUP_ORDER : STAFF_NAV_GROUP_ORDER
  const expandedStorageKey = sidebarExpandedStorageKey(portal)
  const validGroupKeys = useMemo(() => new Set<NavGroupKey>(groupOrder), [groupOrder])

  const [expandedGroups, setExpandedGroups] = useState(() =>
    loadSidebarExpandedGroups(expandedStorageKey, validGroupKeys),
  )

  useEffect(() => {
    setExpandedGroups(loadSidebarExpandedGroups(expandedStorageKey, validGroupKeys))
  }, [expandedStorageKey, validGroupKeys])

  const persistExpanded = useCallback(
    (groups: Set<NavGroupKey>) => persistSidebarExpandedGroups(expandedStorageKey, groups),
    [expandedStorageKey],
  )

  const navigation = useMemo(() => {
    const omitSupport = (item: NavigationItemConfig) =>
      supportChatEnabled ||
      (item.href !== '/dashboard/support' && item.href !== '/customer/support')
    const omitLeases = (item: NavigationItemConfig) =>
      rentalEnabled || item.href !== '/dashboard/leases'

    if (isCustomer) {
      return customerNavigationConfig
        .filter(omitSupport)
        .filter((item) => isCustomerPortalHrefEnabled(customerPortalSettings, item.href))
    }
    const base = staffNavigationConfig
      .filter((item) =>
        canAccessNavHref(item.href, {
          role: role ?? user?.role,
          permissions,
          isPlatformShop: shopContext?.is_platform_shop === true,
          planCode,
          planFeatures,
        }),
      )
      .filter(omitSupport)
      .filter(omitLeases)
    if (user?.role === 'super_admin') {
      base.push(
        {
          nameKey: 'platformAdmin',
          href: '/dashboard/admin',
          icon: ServerIcon,
          groupKey: 'platform',
        },
        {
          nameKey: 'platformSubscriptions',
          href: '/dashboard/admin/subscriptions',
          icon: CreditCardIcon,
          groupKey: 'platform',
        },
        {
          nameKey: 'hostBackups',
          href: '/dashboard/admin/host-backups',
          icon: ArchiveBoxIcon,
          groupKey: 'platform',
        },
        {
          nameKey: 'platformNotifications',
          href: '/dashboard/admin/notifications',
          icon: BellIcon,
          groupKey: 'platform',
        },
        {
          nameKey: 'platformNurture',
          href: '/dashboard/admin/nurture',
          icon: EnvelopeIcon,
          groupKey: 'platform',
        },
        {
          nameKey: 'platformTicketQueue',
          href: '/dashboard/admin/support',
          icon: QueueListIcon,
          groupKey: 'platform',
        },
      )
      // Cross-tenant remittance reports require platform-shop session (not Act-as-tenant).
      if (shopContext?.is_platform_shop === true) {
        base.splice(2, 0, {
          nameKey: 'platformReports',
          href: '/dashboard/admin/reports',
          icon: ChartBarIcon,
          groupKey: 'platform',
        })
      }
    } else if (shopContext?.is_platform_shop === true) {
      base.push({
        nameKey: 'platformTicketQueue',
        href: '/dashboard/admin/support',
        icon: QueueListIcon,
        groupKey: 'platform',
      })
    }
    return base
  }, [isCustomer, user?.role, user?.shop_id, user?.is_web_user, permissions, role, supportChatEnabled, rentalEnabled, customerPortalSettings, shopContext?.is_platform_shop, planCode, planFeatures])

  const navSearchNorm = navSearchQuery.trim().toLowerCase()

  const labelForItem = (item: NavigationItemConfig) => tNav(`nav.${item.nameKey}`)
  const labelForGroup = (groupKey: NavGroupKey) => tNav(`navGroups.${groupKey}`)

  const itemMatchesSearch = useCallback(
    (item: NavigationItemConfig) => {
      if (!navSearchNorm) return true
      const label = labelForItem(item).toLowerCase()
      if (label.includes(navSearchNorm)) return true
      if (item.nameKey.toLowerCase().includes(navSearchNorm)) return true
      return false
    },
    [navSearchNorm, tNav],
  )

  const filteredNavigation = useMemo(
    () => (navSearchNorm ? navigation.filter(itemMatchesSearch) : navigation),
    [navigation, navSearchNorm, itemMatchesSearch],
  )

  const pinnedItems = useMemo(
    () => filteredNavigation.filter((item) => item.pinned),
    [filteredNavigation],
  )

  /** Staff Support Chat etc. — bottom of sidebar above Help, not in a collapsible group. */
  const footerItems = useMemo(
    () => navigation.filter((item) => item.footer),
    [navigation],
  )

  const groupedNavigation = useMemo(() => {
    const groups = {} as Record<NavGroupKey, NavigationItemConfig[]>
    for (const item of filteredNavigation) {
      if (item.pinned || item.footer || !item.groupKey) continue
      if (!groups[item.groupKey]) groups[item.groupKey] = []
      groups[item.groupKey].push(item)
    }
    return groups
  }, [filteredNavigation])

  const activeGroupKey = useMemo(
    () => activeNavGroupForPath(navigation, pathname),
    [navigation, pathname],
  )

  useEffect(() => {
    if (!activeGroupKey || navSearchNorm) return
    setExpandedGroups((prev) => {
      if (prev.has(activeGroupKey)) return prev
      const next = new Set(prev)
      next.add(activeGroupKey)
      persistExpanded(next)
      return next
    })
  }, [activeGroupKey, navSearchNorm, persistExpanded])

  const toggleGroup = (groupKey: NavGroupKey) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      persistExpanded(next)
      return next
    })
  }

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${MD_MIN}px)`)
    const sync = () => {
      const desktop = mq.matches
      setIsDesktopViewport(desktop)
      if (desktop) {
        onClose()
        releaseStuckModalOverlays()
      }
    }
    sync()
    mq.addEventListener('change', sync)
    return () => {
      mq.removeEventListener('change', sync)
      releaseStuckModalOverlays()
    }
  }, [onClose])

  /** Overlay flyout is mobile-only. Desktop collapse uses hamburger → pin restore. */
  const flyoutOpen = isOpen && !isDesktopViewport
  const showPinnedDesktopSidebar = !desktopCollapsed

  const collapseDesktopMenu = () => {
    onDesktopCollapsedChange?.(true)
    onClose()
  }

  const navLinkClass = (isActive: boolean, indented = false) =>
    [
      indented ? 'pl-2.5 pr-2' : 'px-2',
      'group flex items-center rounded-md py-2.5 md:py-1.5 text-sm font-medium transition-colors min-h-11 md:min-h-0',
      isActive
        ? shopPrimary
          ? 'font-medium'
          : 'bg-primary-50 text-primary-700'
        : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900',
    ].join(' ')

  const navLinkStyle = (isActive: boolean): CSSProperties | undefined => {
    if (!shopPrimary || !isActive) return undefined
    return {
      backgroundColor: `color-mix(in srgb, ${shopPrimary} 12%, white)`,
      color: shopPrimary,
      boxShadow: `inset 3px 0 0 0 ${shopPrimary}`,
    }
  }

  const navIconClass = (isActive: boolean) =>
    [
      'mr-3 h-5 w-5 flex-shrink-0 transition-colors',
      isActive
        ? shopPrimary
          ? ''
          : 'text-primary-600'
        : 'text-gray-400 group-hover:text-gray-600',
    ].join(' ')

  const navIconStyle = (isActive: boolean): CSSProperties | undefined => {
    if (!shopPrimary || !isActive) return undefined
    return { color: shopPrimary }
  }

  const handleNavLinkClick = (onNavClick?: () => void) => () => {
    releaseStuckModalOverlays()
    onNavClick?.()
  }

  const supportBadgeCount = supportUnreadThreads > 0 ? supportUnreadThreads : 0
  const isSupportNavItem = (item: NavigationItemConfig) =>
    item.href === '/dashboard/support' || item.href === '/customer/support'

  /** Exact match, or prefix match unless a longer sibling nav href owns the path. */
  const isNavItemActive = (item: NavigationItemConfig) => {
    if (item.href === '/dashboard' || item.href === '/customer') {
      return pathname === item.href
    }
    if (pathname === item.href) return true
    if (!pathname.startsWith(`${item.href}/`)) return false
    return !navigation.some(
      (other) =>
        other.href !== item.href &&
        other.href.startsWith(`${item.href}/`) &&
        (pathname === other.href || pathname.startsWith(`${other.href}/`)),
    )
  }

  const renderNavLink = (item: NavigationItemConfig, onNavClick?: () => void, indented = false) => {
    const active = isNavItemActive(item)
    return (
      <NavLink
        key={item.nameKey}
        to={item.href}
        end
        data-testid="sidebar-nav-link"
        data-nav-href={item.href}
        className={navLinkClass(active, indented)}
        style={navLinkStyle(active)}
        aria-current={active ? 'page' : undefined}
        onClick={handleNavLinkClick(onNavClick)}
      >
        <item.icon className={navIconClass(active)} style={navIconStyle(active)} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">{labelForItem(item)}</span>
        {isSupportNavItem(item) && supportBadgeCount > 0 ? (
          <span className="ml-2 inline-flex h-5 min-w-5 flex-shrink-0 items-center justify-center rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white">
            {supportBadgeCount > 99 ? '99+' : supportBadgeCount}
          </span>
        ) : null}
      </NavLink>
    )
  }

  const renderHelpFooter = (onNavClick?: () => void) => {
    if (isCustomer) return null
    return (
      <div className="mt-4 space-y-0.5 border-t border-gray-200 px-2 pt-3">
        {footerItems.map((item) => renderNavLink(item, onNavClick))}
        <a
          href="/help"
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 md:min-h-0"
          onClick={onNavClick}
        >
          <QuestionMarkCircleIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
          {tCommon('sidebar.helpCenter')}
        </a>
      </div>
    )
  }

  const renderNav = (onNavClick?: () => void) => {
    const mainItems = filteredNavigation.filter((item) => !item.footer)

    if (navSearchNorm) {
      if (mainItems.length === 0) {
        if (footerItems.some(itemMatchesSearch)) return null
        return <p className="px-2 py-3 text-sm text-gray-500">{tCommon('sidebar.noMenuMatches')}</p>
      }
      return mainItems.map((item) => renderNavLink(item, onNavClick))
    }

    if (mainItems.length === 0 && footerItems.length === 0) {
      return <p className="px-2 py-3 text-sm text-gray-500">{tCommon('sidebar.noMenuMatches')}</p>
    }

    return (
      <>
        {pinnedItems.length > 0 ? (
          <div className="mb-3 space-y-0.5 border-b border-gray-200/80 pb-3">
            {pinnedItems.map((item) => renderNavLink(item, onNavClick))}
          </div>
        ) : null}
        {groupOrder.map((groupKey) => {
          const items = groupedNavigation[groupKey]
          if (!items?.length) return null
          const expanded = expandedGroups.has(groupKey)
          const panelId = navGroupPanelId(groupKey)
          const groupHasActiveItem = items.some((item) => navItemMatchesPath(item, pathname))
          const groupHasSupportUnread = items.some(
            (item) => isSupportNavItem(item) && supportBadgeCount > 0,
          )
          return (
            <div key={groupKey} className="mt-3 first:mt-0">
              <button
                type="button"
                onClick={() => toggleGroup(groupKey)}
                data-testid={`sidebar-nav-group-${groupKey}`}
                aria-expanded={expanded}
                aria-controls={panelId}
                className={[
                  'flex w-full items-center justify-between rounded-md border-l-2 py-2 pl-2.5 pr-2 text-left text-[11px] font-semibold uppercase tracking-wider transition-colors',
                  groupHasActiveItem ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500',
                  expanded
                    ? 'text-gray-700'
                    : 'hover:bg-gray-100/80 hover:text-gray-700',
                ].join(' ')}
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  {groupHasActiveItem ? (
                    <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-500" aria-hidden="true" />
                  ) : null}
                  <span className="truncate">{labelForGroup(groupKey)}</span>
                  {!expanded && groupHasSupportUnread ? (
                    <span
                      className="h-2 w-2 flex-shrink-0 rounded-full bg-rose-600"
                      title={`${supportBadgeCount} unread support`}
                      aria-label={`${supportBadgeCount} unread support conversations`}
                    />
                  ) : null}
                </span>
                {expanded ? (
                  <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                )}
                <span className="sr-only">
                  {expanded ? tCommon('sidebar.collapseGroup') : tCommon('sidebar.expandGroup')}
                </span>
              </button>
              {expanded ? (
                <div
                  id={panelId}
                  className="mt-1 ml-3 space-y-0.5 border-l border-gray-200 pl-2"
                >
                  {items.map((item) => renderNavLink(item, onNavClick, true))}
                </div>
              ) : null}
            </div>
          )
        })}
      </>
    )
  }

  const renderSearchBox = (searchInputId: string) => (
    <div className="relative mt-4 px-2">
      <label htmlFor={searchInputId} className="sr-only">
        {tCommon('sidebar.searchMenu')}
      </label>
      <MagnifyingGlassIcon
        className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
        aria-hidden="true"
      />
      <input
        id={searchInputId}
        type="search"
        value={navSearchQuery}
        onChange={(e) => setNavSearchQuery(e.target.value)}
        placeholder={tCommon('sidebar.searchMenu')}
        className="block w-full rounded-md border border-gray-300 bg-white py-1.5 pl-9 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        autoComplete="off"
        spellCheck={false}
      />
      {navSearchQuery ? (
        <button
          type="button"
          onClick={() => setNavSearchQuery('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600"
          aria-label={tCommon('sidebar.clearSearch')}
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )

  const renderBrandRow = (opts: { showCollapse?: boolean }) => {
    // Customer portal brand lives in the top Header — sidebar is nav-only.
    if (isCustomer) {
      if (!opts.showCollapse) return null
      return (
        <div className="flex flex-shrink-0 items-center justify-end border-b border-gray-200/80 px-3 py-2">
          <button
            type="button"
            onClick={collapseDesktopMenu}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
            title={tCommon('sidebar.collapseMenu')}
            aria-label={tCommon('sidebar.collapseMenu')}
          >
            <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      )
    }

    return (
      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-gray-200/80 px-4 pb-4">
        <h1 className="text-lg font-bold tracking-tight text-gray-900 truncate">{tCommon('appName')}</h1>
        {opts.showCollapse ? (
          <button
            type="button"
            onClick={collapseDesktopMenu}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
            title={tCommon('sidebar.collapseMenu')}
            aria-label={tCommon('sidebar.collapseMenu')}
          >
            <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <>
      <Transition show={flyoutOpen} as={Fragment}>
        <Dialog open={flyoutOpen} onClose={onClose} className="relative z-40">
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-75" aria-hidden="true" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative flex max-w-xs flex-1 flex-col bg-gray-50">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute top-0 right-0 -mr-12 pt-2">
                      <button
                        type="button"
                        className="ml-1 flex h-11 w-11 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                        onClick={onClose}
                      >
                        <span className="sr-only">{tCommon('header.closeSidebar')}</span>
                        <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className="flex-1 overflow-y-auto pt-5 pb-4">
                    {isCustomer ? null : renderBrandRow({})}
                    {renderSearchBox('sidebar-nav-search-mobile')}
                    <nav className="mt-3 space-y-1 px-2">{renderNav(onClose)}</nav>
                    {renderHelpFooter(onClose)}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
              <div className="w-14 flex-shrink-0" aria-hidden="true" />
            </div>
        </Dialog>
      </Transition>

      {showPinnedDesktopSidebar ? (
        <div
          className={[
            'hidden md:flex md:w-64 md:flex-col md:z-20',
            isCustomer
              ? 'md:relative md:h-full md:shrink-0'
              : 'md:fixed md:inset-y-0',
          ].join(' ')}
        >
          <div className="flex min-h-0 flex-1 flex-col border-r border-gray-200 bg-gray-50">
            <div
              className={[
                'flex flex-1 flex-col overflow-y-auto pb-4',
                isCustomer ? 'pt-2' : 'pt-5',
              ].join(' ')}
            >
              {renderBrandRow({ showCollapse: true })}
              {renderSearchBox('sidebar-nav-search-desktop')}
              <nav className="mt-3 flex-1 space-y-1 px-2">{renderNav()}</nav>
              {renderHelpFooter()}
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default Sidebar
